import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Truck, AlertOctagon, ShieldAlert, Trash2, Boxes, Search, ArrowUpDown,
  ArrowDownToLine, Mail, Repeat, Sparkles, Star, Printer, Download
} from 'lucide-react';
import { toast } from 'sonner';
import VendorMassRebidDialog from '../components/VendorMassRebidDialog';
import { openBrandedPrintWindow, PrintHelpers as H } from '../utils/brandedPrint';
import { csvCell } from '../lib/utils';

const KpiCard = ({ icon: Icon, label, value, accent, testId }) => (
  <Card className="industrial-card" data-testid={testId}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 flex items-center justify-center rounded-sm ${accent}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-2xl font-bold text-slate-900 mono">{value}</div>
      </div>
    </CardContent>
  </Card>
);

const riskBucket = (row) => {
  if (row.active_recalls > 0) return { label: 'Critical', cls: 'bg-red-100 text-red-700 border-red-200' };
  if (row.defect_rate_pct >= 5 || row.disposal_events >= 3) return { label: 'High', cls: 'bg-orange-100 text-orange-700 border-orange-200' };
  if (row.defect_rate_pct > 0) return { label: 'Watch', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
  return { label: 'Healthy', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
};

const VendorScorecardPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('risk_score');
  const [sortDir, setSortDir] = useState('desc');
  const [rebidVendor, setRebidVendor] = useState(null);
  // iter43 — Scorecard Auto-Nudge
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [nudgeData, setNudgeData] = useState(null);
  const [nudgeRunning, setNudgeRunning] = useState(false);
  const [nudgeThreshold, setNudgeThreshold] = useState(3);

  const runNudge = async (apply = false) => {
    setNudgeRunning(true);
    try {
      const r = await api().post('/vendors/scorecard-nudge/run', { threshold: nudgeThreshold, apply });
      setNudgeData(r.data);
      if (apply) {
        toast.success(`Applied ${r.data.totals.applied} rating change(s)`);
        fetchData();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Nudge failed'); }
    finally { setNudgeRunning(false); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api().get('/vendors/scorecard');
      setData(data);
    } catch {
      toast.error('Failed to load scorecard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  if (loading || !data) {
    return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;
  }

  const filtered = data.rows
    .filter(r => r.vendor_name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      if (typeof av === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });

  // iter67 — branded printable scorecard report
  const printScorecard = async () => {
    const cols = [
      { key: 'vendor_name', label: 'Vendor' },
      { key: 'star_rating', label: 'Stars', mono: true, fmt: (v) => v ? `${v}★` : '—' },
      { key: 'total_received', label: 'Received', mono: true },
      { key: 'disposal_events', label: 'Disposals', mono: true },
      { key: 'defect_rate_pct', label: 'Defect %', mono: true, fmt: (v) => `${(v || 0).toFixed(2)}%` },
      { key: 'active_recalls', label: 'Recalls', mono: true },
      { key: 'risk_score', label: 'Risk', mono: true, fmt: (v) => Number(v || 0).toFixed(1) },
      { key: 'risk_bucket', label: 'Bucket', fmt: (_, r) => riskBucket(r).label },
    ];
    const summary = `<strong>Vendors:</strong> ${data.totals.vendors} &nbsp;·&nbsp;
                     <strong>With disposals:</strong> ${data.totals.vendors_with_disposals} &nbsp;·&nbsp;
                     <strong>With active recalls:</strong> ${data.totals.vendors_with_active_recalls}`;
    await openBrandedPrintWindow({
      title: 'VENDOR SCORECARD',
      subtitle: `${filtered.length} vendor(s)${search ? ` · "${search}"` : ''}`,
      orientation: 'landscape',
      body: `${H.callout(summary)}${H.table(cols, filtered)}`,
      footerNote: 'Internal Document · Vendor Defect Scorecard',
    });
  };

  const downloadScorecardCsv = () => {
    const headers = ['Vendor', 'Stars', 'Received', 'Disposals', 'Defect %', 'Active Recalls', 'Risk Score', 'Risk Bucket'];
    const rows = filtered.map(r => [
      r.vendor_name, r.star_rating || '', r.total_received, r.disposal_events,
      (r.defect_rate_pct || 0).toFixed(2), r.active_recalls,
      (r.risk_score || 0).toFixed(1), riskBucket(r).label,
    ]);
    const csv = [headers, ...rows].map(row => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendor-scorecard-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${rows.length} vendors`);
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortHead = ({ k, children, align = 'left' }) => (
    <TableHead
      onClick={() => toggleSort(k)}
      className={`cursor-pointer select-none ${align === 'right' ? 'text-right' : ''}`}
      data-testid={`sort-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown className={`w-3 h-3 ${sortKey === k ? 'text-orange-500' : 'text-slate-300'}`} />
      </span>
    </TableHead>
  );

  return (
    <div className="space-y-6 animate-slideIn" data-testid="vendor-scorecard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vendor Defect Scorecard</h1>
          <p className="text-slate-500 text-sm">Defect rate, disposals, and active recalls per supplier — sorted by risk.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => printScorecard()} variant="outline" className="rounded-sm" data-testid="scorecard-print-btn">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <Button onClick={() => downloadScorecardCsv()} variant="outline" className="rounded-sm" data-testid="scorecard-csv-btn">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
          <Button onClick={() => { setNudgeOpen(true); setNudgeData(null); }} className="bg-amber-500 hover:bg-amber-600 rounded-sm" data-testid="scorecard-nudge-btn">
            <Sparkles className="w-4 h-4 mr-1" /> Run Auto-Nudge
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Truck} label="Vendors" value={data.totals.vendors} accent="bg-slate-700" testId="kpi-vendors" />
        <KpiCard icon={Trash2} label="With Disposals" value={data.totals.vendors_with_disposals} accent="bg-orange-500" testId="kpi-vendors-with-disposals" />
        <KpiCard icon={ShieldAlert} label="With Active Recalls" value={data.totals.vendors_with_active_recalls} accent="bg-red-600" testId="kpi-vendors-with-recalls" />
        <KpiCard icon={ArrowDownToLine} label="Total Received" value={data.totals.total_received} accent="bg-emerald-600" testId="kpi-total-received" />
      </div>

      {/* Search */}
      <Card className="industrial-card">
        <CardContent className="p-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Filter vendors…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-sm"
              data-testid="vendor-scorecard-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Truck className="w-4 h-4" /> Suppliers Ranked By Risk
          </h2>
          <span className="text-xs text-slate-500 mono">{filtered.length} of {data.rows.length}</span>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-8 text-sm text-slate-500 text-center" data-testid="vendor-scorecard-empty">
              {data.rows.length === 0
                ? 'No vendors yet. Add a vendor and link items to start tracking.'
                : 'No vendors match your filter.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <SortHead k="vendor_name">Vendor</SortHead>
                    <TableHead>Risk</TableHead>
                    <SortHead k="risk_index" align="right">Risk Idx</SortHead>
                    <SortHead k="items_count" align="right">Items</SortHead>
                    <SortHead k="total_received" align="right">Received</SortHead>
                    <SortHead k="total_disposed" align="right">Disposed</SortHead>
                    <SortHead k="defect_rate_pct" align="right">Defect %</SortHead>
                    <SortHead k="disposal_events" align="right">Events</SortHead>
                    <SortHead k="active_recalls" align="right">Recalls</SortHead>
                    <SortHead k="lead_time_days" align="right">Lead (d)</SortHead>
                    <TableHead>Last Disposal</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(r => {
                    const bucket = riskBucket(r);
                    const last = r.last_disposal_at ? new Date(r.last_disposal_at).toLocaleDateString() : '—';
                    return (
                      <TableRow key={r.vendor_id} data-testid={`scorecard-row-${r.vendor_id}`}>
                        <TableCell className="font-medium text-slate-800">{r.vendor_name}</TableCell>
                        <TableCell>
                          <span className={`text-xs uppercase tracking-wider px-2 py-0.5 border rounded-sm ${bucket.cls}`} data-testid={`scorecard-risk-${r.vendor_id}`}>
                            {bucket.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2" data-testid={`scorecard-risk-index-${r.vendor_id}`}>
                            <div className="w-16 h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                              <div
                                className={`h-full ${r.risk_index >= 70 ? 'bg-red-600' : r.risk_index >= 40 ? 'bg-orange-500' : 'bg-emerald-500'}`}
                                style={{ width: `${Math.min(100, r.risk_index)}%` }}
                              />
                            </div>
                            <span className="mono text-xs text-slate-700 w-10 text-right">{r.risk_index}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right mono">{r.items_count}</TableCell>
                        <TableCell className="text-right mono text-emerald-700">{r.total_received}</TableCell>
                        <TableCell className="text-right mono text-red-700">{r.total_disposed}</TableCell>
                        <TableCell className="text-right mono">
                          <span className={r.defect_rate_pct >= 5 ? 'text-red-600 font-semibold' : r.defect_rate_pct > 0 ? 'text-amber-600' : 'text-slate-500'}>
                            {r.defect_rate_pct}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right mono">{r.disposal_events}</TableCell>
                        <TableCell className="text-right mono">
                          {r.active_recalls > 0 ? (
                            <span className="text-red-600 font-bold inline-flex items-center gap-1">
                              <AlertOctagon className="w-3 h-3" /> {r.active_recalls}
                            </span>
                          ) : <span className="text-slate-400">0</span>}
                        </TableCell>
                        <TableCell className="text-right mono text-slate-500">{r.lead_time_days}</TableCell>
                        <TableCell className="text-xs text-slate-500">{last}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => setRebidVendor(r)}
                              className="rounded-sm text-orange-600 hover:bg-orange-50"
                              title="Mass re-bid this vendor"
                              data-testid={`scorecard-rebid-${r.vendor_id}`}
                            >
                              <Repeat className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => navigate('/vendors')}
                              className="rounded-sm"
                              data-testid={`scorecard-view-${r.vendor_id}`}
                            >
                              <Boxes className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400">
        <span className="font-medium">Risk score</span> = defect % + (events × 1.5) + (active recalls × 25). Sort columns to dig deeper.
      </p>

      <VendorMassRebidDialog
        open={!!rebidVendor}
        onOpenChange={(open) => { if (!open) setRebidVendor(null); }}
        vendor={rebidVendor}
      />

      {/* Scorecard Auto-Nudge dialog */}
      {nudgeOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setNudgeOpen(false)}>
          <div className="bg-white rounded-sm max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="scorecard-nudge-dialog">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Vendor Scorecard Auto-Nudge</h2>
                <p className="text-xs text-slate-500">Automatically reduce star rating for vendors with recent disposals or active recalls (last 90 days). Suggests an alternative vendor when rating drops below 3.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setNudgeOpen(false)}>×</Button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-end gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Threshold (events)</label>
                  <Input type="number" min={1} value={nudgeThreshold} onChange={(e) => setNudgeThreshold(parseInt(e.target.value) || 1)} className="rounded-sm w-24 mono" data-testid="scorecard-nudge-threshold" />
                </div>
                <Button onClick={() => runNudge(false)} disabled={nudgeRunning} variant="outline" data-testid="scorecard-nudge-preview"><Search className="w-4 h-4 mr-1" /> Preview</Button>
                {nudgeData && nudgeData.totals.would_change > 0 && (
                  <Button onClick={() => runNudge(true)} disabled={nudgeRunning} className="bg-amber-600 hover:bg-amber-700" data-testid="scorecard-nudge-apply"><Sparkles className="w-4 h-4 mr-1" /> Apply {nudgeData.totals.would_change} Change(s)</Button>
                )}
              </div>
              {nudgeData && (
                <>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="border border-slate-200 rounded-sm p-3"><div className="text-xs uppercase text-slate-500">Evaluated</div><div className="text-2xl font-bold mono">{nudgeData.totals.vendors_evaluated}</div></div>
                    <div className="border border-amber-200 bg-amber-50 rounded-sm p-3"><div className="text-xs uppercase text-amber-700">Would Change</div><div className="text-2xl font-bold mono text-amber-700">{nudgeData.totals.would_change}</div></div>
                    <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3"><div className="text-xs uppercase text-emerald-700">With Alternative</div><div className="text-2xl font-bold mono text-emerald-700">{nudgeData.totals.with_alternative}</div></div>
                  </div>
                  {nudgeData.nudges.filter(n => n.would_change || n.total_events > 0).length === 0 ? (
                    <div className="text-center py-6 text-sm text-slate-400">No vendors meet the threshold. All clear ✨</div>
                  ) : (
                    <Table className="data-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Vendor</TableHead>
                          <TableHead className="text-right">Disposed (90d)</TableHead>
                          <TableHead className="text-right">Active Recalls</TableHead>
                          <TableHead>Rating Δ</TableHead>
                          <TableHead>Suggested Alt.</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {nudgeData.nudges.filter(n => n.would_change || n.total_events > 0).map(n => (
                          <TableRow key={n.vendor_id} data-testid={`nudge-row-${n.vendor_id}`} className={n.would_change ? 'bg-amber-50' : ''}>
                            <TableCell className="font-medium">{n.vendor_name}</TableCell>
                            <TableCell className="text-right mono">{n.disposed_count_90d}</TableCell>
                            <TableCell className={`text-right mono ${n.active_recalls > 0 ? 'text-red-600 font-bold' : ''}`}>{n.active_recalls}</TableCell>
                            <TableCell>
                              {n.would_change ? (
                                <div className="flex items-center gap-1 text-sm">
                                  <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                                  <span className="mono">{n.current_star_rating ?? '—'} → {n.suggested_star_rating}</span>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-400">no change</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs">
                              {n.suggested_alternative ? (
                                <span className="text-emerald-700">{n.suggested_alternative.vendor_name} <span className="text-slate-400">({n.suggested_alternative.star_rating}★)</span></span>
                              ) : <span className="text-slate-300">—</span>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorScorecardPage;
